import {
  mergeTyped,
  useDataStream,
  useStreamValue,
  useVoidEventAsStream
} from 'annotation-tools/utils';
import React, { FC, memo, useCallback, useMemo } from 'react';
import { mapTo } from 'rxjs/operators';
import { updateModel } from '../../../api/fetch-functions/model';
import { ModelAPIRequestBodyType } from '../../../api/types/model/api-request-body';
import { maxStringLength } from '../../../constants/max-string-length';
import { viewTexts } from '../../../constants/view-texts';
import { useFetchResultState } from '../../../functions/hooks/use-fetch-result-state';
import {
  FormInputStateConfigType,
  useFormInputState
} from '../../../functions/hooks/use-form-input-state';
import { useToken } from '../../../global-state/auth-state';
import { fetchModelListOf } from '../../../global-state/database/model-list';
import { IModelType } from '../../../types/record/model';
import { EditModelViewDialog } from './edit-model-dialog-view';

const formInputStateConfig: FormInputStateConfigType<ModelAPIRequestBodyType> = {
  minStringLength: 1,
  maxStringLength: maxStringLength.modelName,
  stringLengthCheckMessageTargetName:
    viewTexts.stringLengthCheckMessageTargetName.inferenceModel,
  required: true,
  validationErrorContentMapFn: content =>
    content.name !== undefined ? content.name.toArray() : []
};

interface Props {
  open: boolean;
  onClose: (reason: 'ok' | 'cancel' | '') => void;
  companyId: number;
  model: IModelType;
}

export const EditModelDialog: FC<Props> = memo(
  ({ open, onClose, companyId, model: modelInit }: Readonly<Props>) => {
    const token = useToken();

    /* state */

    const [
      // dummy comment
      fetchResult,
      setFetchResult
    ] = useFetchResultState<IModelType, ModelAPIRequestBodyType>();

    const nonFieldErrors = useMemo<string[]>(
      () =>
        fetchResult !== undefined &&
        fetchResult.type === 'error' &&
        fetchResult.body.type === 'non-field-errors'
          ? fetchResult.body.content.toArray()
          : [],
      [fetchResult]
    );

    const [
      modelName$,
      modelNameErrors$,
      modelNameIsValid$,
      modelNameOnInput
    ] = useFormInputState<IModelType, ModelAPIRequestBodyType>(
      open,
      fetchResult,
      formInputStateConfig,
      modelInit.name
    );

    const [submitOnClick$, submitOnClick] = useVoidEventAsStream();

    const submitButtonEnabled$ = useDataStream<boolean>(
      false,
      mergeTyped(submitOnClick$.pipe(mapTo(false)), modelNameIsValid$)
    );

    /* extract values */

    const submitButtonEnabled = useStreamValue(submitButtonEnabled$, false);

    const modelName = useStreamValue(modelName$);
    const modelNameErrors = useStreamValue(modelNameErrors$);

    /* event handlers */

    const closeOnClick = useCallback(
      (reason: '' | 'cancel' | 'ok') => {
        if (reason !== 'ok') {
          onClose(reason);
        } else {
          submitOnClick();
          if (token !== undefined && modelName !== undefined) {
            updateModel(token)(modelInit.id)({
              name: modelName,
              companyId,
              adoptedVersionId: undefined
            }).then(res => {
              setFetchResult(res);
              if (res.type === 'success') {
                onClose(reason);
                fetchModelListOf(companyId);
              }
            });
          }
        }
      },
      [
        onClose,
        companyId,
        modelInit,
        modelName,
        token,
        setFetchResult,
        submitOnClick
      ]
    );

    return (
      <EditModelViewDialog
        open={open}
        onClose={closeOnClick}
        modelName={modelName}
        modelNameErrors={modelNameErrors}
        modelNameOnInput={modelNameOnInput}
        submitButtonEnabled={submitButtonEnabled}
        nonFieldErrors={nonFieldErrors}
      />
    );
  }
);

EditModelDialog.displayName = 'EditModelDialog';
